import { AxiosInstance } from "axios";
import Endpoint from "@/utils/endpoint";
import compareTo from "@/utils/compareTo";
import { CustomerSelectValue } from "@/utils/getCustomers";
import Customer, { customerInitValue } from "@/types/customer/Customer";
import Interest from "@/types/app/excelData";
import UserInterface from "@/types/app/IUser";
import ErrorService from "./erroService";
import VuetifySelectItems from "@/types/app/vuetifySelectItems";

export default class CustomerService {
  constructor(
    private api: AxiosInstance,
    private endpoint: Endpoint = new Endpoint()
  ) {}

  async hasLCMRole(email: string) {
    return await this.api
      .get(this.endpoint.getUserByEmail, {
        params: {
          email: email,
        },
      })
      .then((res) => {
        const roles = res.data.roles;
        return roles.includes("lcm") || roles.includes("admin");
      })
      .catch((res) => {
        ErrorService.onError(res);
        return false;
      });
  }

  async getAllUsers(): Promise<Customer[]> {
    return await this.api
      .get(this.endpoint.getAllUsers)
      .then((res) => {
        return res.data as Customer[];
      })
      .catch((res) => {
        ErrorService.onError(res);
        return [];
      });
  }

  async getPagedAllCustomers(skip:number, take:number, orderBy:string): Promise<Customer[]> {
    return await this.api
      .get(`${this.endpoint.pagedCustomers}?status=1&skip=${skip}&take=${take}&orderBy=evaluationDate%20desc`)
      .then((res) => {
        return res.data as Customer[];
      })
      .catch((res) => {
        ErrorService.onError(res);
        return [];
      });
  }

  async getSelectItemsCustomers(status: number): Promise<VuetifySelectItems[]> {
    let filter = '';

    if(status != -1)
         filter = `?status=${status}`
    return await this.api
      .get(`${this.endpoint.selectItemsCustomers}${filter}`)
      .then((res) => {
        return res.data as VuetifySelectItems[];
      })
      .catch((res) => {
        ErrorService.onError(res);
        return [];
      });
  }

  async getSelectAccountCustomers(): Promise<VuetifySelectItems[]> {
    return await this.api
      .get(`${this.endpoint.customers}/select-accounts`)
      .then((res) => {
        return res.data as VuetifySelectItems[];
      })
      .catch((res) => {
        ErrorService.onError(res);
        return [];
      });
  }

  async getSelectManagerCustomers(): Promise<VuetifySelectItems[]> {
    return await this.api
      .get(`${this.endpoint.customers}/select-managers`)
      .then((res) => {
        return res.data as VuetifySelectItems[];
      })
      .catch((res) => {
        ErrorService.onError(res);
        return [];
      });
  }

  async importCip(dataImport: any[]): Promise<any> {
    const result = { hasError: false, messageError: "", data: [] };
    try {
      await this.api
        .post(this.endpoint.importCip, { excelData: dataImport })
        .then((res: any) => {
          result.hasError = res.hasErrors;
          result.data = res.data;
        })
        .catch((error) => {
          result.messageError =
            error.response.data.errors ||
            "Error trying to import CIP";
          result.hasError = error.response.data.hasErrors;
          result.data = error.response.data.data;
        });
    } catch (error) {
      ErrorService.onError(error);
      result.messageError = "Error trying to create an interest";
      result.hasError = true;
    }
    return result;
  }

  async hasInvestmentAccount(id: string) {
    const { data: response } = await this.api.get(
      this.endpoint.customerHasAccount + id
    );
    return response.hasAccount === true;
  }

  async getCustomers({
    filter = "status=1",
  }: { filter?: string } = {}): Promise<CustomerSelectValue[]> {
    try {
      const { data } = await this.api.get<CustomerSelectValue[]>(
        `${this.endpoint.customers}?${filter}`
      );
      const customerOrdened = data.sort((a, b) =>
        compareTo(a.fullName, b.fullName)
      );

      return customerOrdened;
    } catch (error) {
      ErrorService.onError(error);
      return [];
    }
  }

  async getCustomerById(customerId:string): Promise<Customer> {
    try {
      const { data } = await this.api.get<Customer>(
        `${this.endpoint.customerBydId}/${customerId}`
      );
     
      return data;
    } catch (error) {
      ErrorService.onError(error);
      return customerInitValue;
    }
  }

  async saveInterest(interests: Customer): Promise<any> {
    const result = { hasError: false, messageError: "", data: {} };
    try {
      if (!await this.exists(interests.id, interests.email)) {
       this.api
          .post(this.endpoint.createInterest, {
            customer: {
              id: interests.id?.length == 0 ? null : interests.id,
              firstName: interests.firstName,
              lastName: interests.lastName,
              country: interests.country,
              email: interests.email,
              phone: interests.phone,
              investmentVehicle: interests.investmentVehicle,
              withoutUser: true,
              person: {
                classification: interests.classification,
                type: interests.investorType,
                config: [{ text: "Text 1", selected: "true" }],
              },
            },
          })
          .then((res: any) => {
            result.hasError = res.hasErrors;
          })
          .catch((error) => {
            ErrorService.onError(error);
            result.messageError =
              error.response.data.errors![0] ||
              "Error trying to create an interest";
            result.hasError = error.response.data.hasErrors;
          });
      }else {
        result.hasError = true;
        result.messageError = 'There is already a customer registered with this email';
      }
    } catch (error) {
      ErrorService.onError(error);
      result.messageError = "Error trying to create an interest";
      result.hasError = true;
    }
    return result;
  }

  async exists(id: string, email: string): Promise<boolean> {
   
   
     return await this.api
        .post(this.endpoint.customersFilter, {
          email: email,
        })
        .then((res: any) => {
          if (!res.hasErrors && res.data) 
          {
            const result = res.data.id != id;
            return result;
          }
          else return false;
        })
        .catch((error) => {
          return false;
        });
 
  }

  async saveInterests(interests: Interest[]): Promise<any> {
    const result = { hasError: false, messageError: "", data: [] };
    try {
      await this.api
        .post(this.endpoint.importInterest, { customers: interests })
        .then((res: any) => {
          result.hasError = res.hasErrors;
          result.data = res.data;
        })
        .catch((error) => {
          result.messageError =
            error.response.data.errors ||
            "Error trying to create an interest";
          result.hasError = error.response.data.hasErrors;
        });
    } catch (error) {
      ErrorService.onError(error);
      result.messageError = "Error trying to create an interest";
      result.hasError = true;
    }
    return result;
  }
}
